import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { backButtonManager, BackHandler } from '../utils/backButtonManager';

/**
 * Hook to register a back action handler (for Android system back button / back swipe gesture).
 * Also hooks into browser history popstate when running in web browsers.
 * 
 * @param handler Callback to execute when back is triggered. Return false to yield to next handler.
 * @param enabled Whether this handler is currently active.
 * @param priority Higher priority handlers execute first (default: 100).
 */
export function useBackButton(
  handler: BackHandler,
  enabled: boolean = true,
  priority: number = 100
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;

    // 1. Register with the global BackButtonManager (Capacitor native backButton event)
    const unregister = backButtonManager.register(() => {
      return handlerRef.current();
    }, priority);

    // 2. In browser environments (mobile web / desktop), push dummy state so browser back swipe works
    let cleanupBrowser: (() => void) | undefined;

    if (!Capacitor.isNativePlatform()) {
      const stateId = `cc_modal_${Math.random().toString(36).substring(2, 9)}`;
      let isPoppedByBrowser = false;

      // Push dummy state
      window.history.pushState({ [stateId]: true }, '');

      const handlePopState = () => {
        isPoppedByBrowser = true;
        handlerRef.current();
      };

      window.addEventListener('popstate', handlePopState);

      cleanupBrowser = () => {
        window.removeEventListener('popstate', handlePopState);
        // Clean up dummy history state if closed via button instead of browser back
        if (!isPoppedByBrowser && window.history.state && window.history.state[stateId]) {
          window.history.back();
        }
      };
    }

    return () => {
      unregister();
      if (cleanupBrowser) cleanupBrowser();
    };
  }, [enabled, priority]);
}

export default useBackButton;
